import type { CSSProperties } from 'react';

import { MAX_WIDTH } from '../constants';

export const getWidthStyleVariables = ({
  width,
  width_max,
  config,
  variableMap,
}: {
  width?: string | number | '100%' | '100dvw' | 'auto' | 'fit-content';
  width_max?: '100%';
  config?: {
    isScrollHorizontalOverflow?: boolean;
  };
  variableMap: {
    width: string;
    maxWidth: string;
  };
}) => {
  const { width: widthStyle, maxWidth: maxWidthStyle } = getWidthStyle(width, width_max, config);

  return {
    [variableMap.width]: widthStyle,
    [variableMap.maxWidth]: maxWidthStyle,
  };
};

export const getWidthStyle = (
  width?: string | number | '100%' | '100dvw' | 'auto' | 'fit-content',
  width_max?: '100%',
  config?: {
    isScrollHorizontalOverflow?: boolean;
  },
): Pick<CSSProperties, 'width' | 'maxWidth'> => {
  const maxWidthPX = `${MAX_WIDTH}px`;

  if (!width && !width_max) {
    // return default
    return {
      width: '100%',
      maxWidth: `min(100%, ${maxWidthPX})`,
    };
  }

  if (width === 'auto') {
    return {
      width: 'auto',
      maxWidth: '100%',
    };
  }

  if (width === 'fit-content') {
    return {
      width: 'fit-content',
      maxWidth: 'fit-content',
    };
  }

  const widthWithUnit = typeof width === 'number' ? `${width}px` : width;

  const isFullWidth = width_max === '100%';

  let elWidth = isFullWidth ? width_max : `${widthWithUnit || maxWidthPX}`;
  if (width_max === '100%' && config?.isScrollHorizontalOverflow) {
    elWidth = maxWidthPX;
  }

  return {
    maxWidth: elWidth,
    width: '100%',
  };
};

export const getHeightStyle = (height?: number | '100%' | 'stretch' | 'auto'): Pick<CSSProperties, 'height'> => {
  if (typeof height === 'string') {
    if (height === 'stretch') {
      return {
        height: 'auto',
      };
    }
    return {
      height: height,
    };
  }
  return {
    height: height ? `${height}px` : 'auto',
  };
};

export const getAspectRatioStyle = (
  width?: number | '100%',
  height?: number,
  aspectRatio?: number,
): Pick<CSSProperties, 'aspectRatio'> => {
  let value: CSSProperties['aspectRatio'];

  if (width && height) {
    value = `${width} / ${height}`;
  } else if (aspectRatio) {
    value = `${aspectRatio} / 1`;
  } else if (width === '100%') {
    value = undefined;
  }

  return { aspectRatio: value };
};
